import React from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Column,
  Table,
  TableContainer,
} from '@odin-labs/components';
import {
  AccessGroupSummaryFragment,
  SitePermission,
  useGetSiteAccessGroupsQuery,
} from 'apollo/generated/client-operations';
import { LoadingError } from 'components/loadingError';
import { NewHeader } from 'components/header/NewHeader';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import { AccessGroupDetails } from 'accessGroups/AccessGroupDetails';
import { AccessGroupEditor } from 'accessGroups/AccessGroupEditor';
import {
  AccessGroupsError,
  ActionFeedback,
  AccessGroupStatus,
  GroupZones,
} from 'accessGroups/AccessGroupStatus';
import { modeDescriptions } from 'accessGroups/presentation';
import { useSitePermissions } from 'sitePermissions/useSitePermissions';

function getGroupColumns({
  onSelect,
  timeZone,
}: {
  timeZone: string;
  onSelect: (id: string) => void;
}): Column<AccessGroupSummaryFragment>[] {
  return [
    {
      id: 'name',
      Header: 'Group',
      accessor: 'name',
      Cell: ({ row }) => (
        <div className="odin-space-y-2">
          <Button
            text={row.original.name}
            theme="naked"
            size="sm"
            onClick={() => onSelect(row.original.id)}
          />
          {row.original.isDefault && <Badge text="Default" color="blue" />}
          {row.original.archivedAt && <Badge text="Archived" color="gray" />}
        </div>
      ),
    },
    {
      id: 'zones',
      Header: 'Zones',
      Cell: ({ row }) => <GroupZones zones={row.original.zones} />,
    },
    { id: 'members', Header: 'Members', accessor: 'memberCount' },
    {
      id: 'sync',
      Header: 'Avigilon configuration',
      Cell: ({ row }) => (
        <AccessGroupStatus sync={row.original.avigilon} timeZone={timeZone} />
      ),
    },
  ];
}

function SiteAccessGroups({
  siteId,
  onTabApiChange,
  onIsDirtyChange,
}: {
  siteId: string;
  onTabApiChange: JobsiteConfigTabProps['onTabApiChange'];
  onIsDirtyChange: JobsiteConfigTabProps['onIsDirtyChange'];
}): React.ReactElement {
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [savedMessage, setSavedMessage] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [groupId, setGroupId] = React.useState<string | null>(null);
  const { data, previousData, loading, error, refetch } =
    useGetSiteAccessGroupsQuery({
      variables: { siteId, includeArchived },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    });
  const visibleData = data ?? (loading ? previousData : undefined);
  const site = visibleData?.getJobsite;
  const timeZone = site?.timeZone ?? 'UTC';
  React.useEffect(() => onIsDirtyChange(false), [onIsDirtyChange]);
  React.useEffect(
    () =>
      onTabApiChange({
        refetchData: refetch,
        loading,
        jobsiteName: site?.name,
      }),
    [onTabApiChange, refetch, loading, site?.name],
  );
  const columns = React.useMemo(
    () => getGroupColumns({ onSelect: setGroupId, timeZone }),
    [timeZone],
  );
  if (error)
    return (
      <>
        <ActionFeedback message={savedMessage} />
        <AccessGroupsError
          retry={() => {
            void refetch().catch((): void => undefined);
          }}
        />
      </>
    );
  return (
    <>
      <ActionFeedback message={savedMessage} />
      <TableContainer>
        <NewHeader
          title="Access groups"
          size="md"
          actionsProps={{
            headerActions: [],
            children: (
              <Button
                text="Create access group"
                size="sm"
                disabled={!site}
                onClick={() => setCreating(true)}
              />
            ),
          }}
        />
        <div className="odin-px-5 odin-pb-5 odin-space-y-4">
          <p className="odin-text-sm odin-text-gray-600">
            Define which zones each group grants, then manage memberships on
            worker profiles.
          </p>
          {site && (
            <p className="odin-rounded-lg odin-bg-gray-50 odin-p-3 odin-text-sm">
              {modeDescriptions[site.zonesMode]}
            </p>
          )}
          <div className="odin-flex odin-items-center odin-gap-3">
            <Checkbox
              name="include-archived-groups"
              label="Show archived groups"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            {loading && site && (
              <span
                role="status"
                aria-label="Refreshing access groups"
                className="odin-text-sm odin-text-gray-500"
              >
                Refreshing access groups…
              </span>
            )}
          </div>
        </div>
        <Table
          enablePagination
          loading={loading && !site}
          columns={columns}
          data={site?.accessGroups ?? []}
          noResultsText="No access groups. Create a group to configure zone access."
          disableGlobalFilter
        />
      </TableContainer>
      {creating && (
        <AccessGroupEditor
          siteId={siteId}
          refresh={refetch}
          onSaved={setSavedMessage}
          onClose={() => setCreating(false)}
        />
      )}
      {groupId && site && (
        <AccessGroupDetails
          key={`${siteId}:${groupId}`}
          siteId={siteId}
          groupId={groupId}
          timeZone={timeZone}
          zones={visibleData.siteConfigurationZones}
          onSaved={setSavedMessage}
          refreshList={refetch}
          onClose={() => setGroupId(null)}
        />
      )}
    </>
  );
}

export function AccessGroupsTab({
  jobsiteId: siteId,
  onTabApiChange,
  onIsDirtyChange,
}: JobsiteConfigTabProps): React.ReactElement {
  const sitePermissions = useSitePermissions(siteId);
  if (sitePermissions.loading) return <LoadingError loading />;
  if (!sitePermissions.can(SitePermission.ViewSiteGroups))
    return (
      <p className="odin-p-6">
        You do not have permission to view access groups.
      </p>
    );
  return (
    <SiteAccessGroups
      key={siteId}
      siteId={siteId}
      onTabApiChange={onTabApiChange}
      onIsDirtyChange={onIsDirtyChange}
    />
  );
}
